import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useSendbirdStateContext } from '@sendbird/uikit-react/useSendbirdStateContext';
import {
  GroupChannelFilter,
  GroupChannelCollection,
  GroupChannelCollectionEventHandler,
  QueryType,
} from '@sendbird/chat/groupChannel';
import type { GroupChannel } from '@sendbird/chat/groupChannel';
import type { BaseChannel } from '@sendbird/chat';
import { pipe, Option, Array as A, MutableRef } from 'effect';
import type { List } from 'immutable';

import { userProfileSelector } from '@/domain/env';
// eslint-disable-next-line no-restricted-syntax
import { loadedDataCompanyIdSelector, documentDocumentIdSelector } from '@/domain/documents';
import { chatParticipantsSelector } from '@/domain/chat';
import { yieldErrorNotification } from '@/lib/toasts';
import { Apps } from '@/domain/env/helpers';
import { buildChannelContext, resolveChannelContext } from '@/components/mui/Layouts/components/Chat/helpers';

/**
 * A company chat peer eligible to appear in the user directory. Plain-object projection of the
 * domain `ChatUser` Immutable Record (`@/domain/chat/helpers.js` `ChatUserFactory`), read via
 * `.get()` at the selector boundary so no `any` leaks into the component tree. `userGUID` is
 * the Sendbird user id (matches `userProfile.id` and channel member ids); `userId` is the email.
 */
export interface DirectoryUser {
  userGUID: string;
  userId: string;
  username: string;
  firstName: string;
  lastName: string;
  picture: string;
  isSupport: boolean;
}

/**
 * The domain participant store is an untyped Immutable `List<Record>`. Narrow it structurally
 * to the fields the directory needs, at the boundary, instead of casting at call sites.
 */
interface ChatParticipantRecord {
  get(key: 'userGUID' | 'userId' | 'username' | 'firstName' | 'lastName' | 'picture'): string;
  get(key: 'isSupport'): boolean;
}

const toDirectoryUser = (record: ChatParticipantRecord): DirectoryUser => ({
  userGUID: record.get('userGUID'),
  userId: record.get('userId'),
  username: record.get('username'),
  firstName: record.get('firstName'),
  lastName: record.get('lastName'),
  picture: record.get('picture'),
  isSupport: record.get('isSupport'),
});

// The single source of truth for a directory user's name — used for BOTH the row label
// (UserPreviewItem) and the search/filter below, so the two can never diverge (a name shown
// in the list is always matchable by the same text in the search box). Per DA-19674 the row
// shows the user's full name: prefer the assembled "First Last" (or just the first name when
// there is no last name), falling back to the app username when neither is present.
export const directoryUserName = (user: DirectoryUser): string =>
  [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.username;

// A direct channel "matches" the active context when its resolved context is a `direct` chat
// whose companyId (and documentId, on a document page) equals the active context — the same
// equality the ChannelList dedupe uses. Returns the counterpart userGUID (the member that is
// not the current user) when it matches, else null.
const matchingDirectCounterpart = (
  channel: Pick<GroupChannel, 'cachedMetaData' | 'members'>,
  currentUserGuid: string,
  companyId: string,
  documentId: string | null,
): string | null => {
  const ctx = resolveChannelContext(channel);
  if (ctx?.type !== 'direct') return null;
  if (String(ctx.companyId ?? '') !== String(companyId)) return null;
  if (documentId ? String(ctx.documentId ?? '') !== String(documentId) : false) return null;
  const other = channel.members.find((m) => m.userId !== currentUserGuid);
  return other?.userId ?? null;
};

const deriveExcludedGuids = (
  channels: ReadonlyArray<GroupChannel>,
  currentUserGuid: string,
  companyId: string,
  documentId: string | null,
): ReadonlySet<string> =>
  pipe(
    channels,
    A.filterMap((channel) =>
      Option.fromNullable(matchingDirectCounterpart(channel, currentUserGuid, companyId, documentId)),
    ),
    (guids) => new Set(guids),
  );

export interface UseChatUserDirectoryResult {
  /** True once every channel page for the active context has been retrieved. */
  isFullyLoaded: boolean;
  /** Company peers with no matching direct chat in context, minus self/support, name-filtered. */
  eligibleUsers: ReadonlyArray<DirectoryUser>;
  /** True while a chat is being created from a user row (interaction lock + loader). */
  isCreating: boolean;
  /** Start (or open the existing) direct chat with the given user in the active context. */
  startChat: (user: DirectoryUser) => Promise<void>;
}

interface UseChatUserDirectoryArgs {
  /** Debounced search text from the panel; filters the user list by name (same text as chats). */
  searchText: string;
  /** Selects a channel as active (the ChannelList `onSelect`). */
  onSelect: (url: string) => void;
  /**
   * When false, the hook does NO work: it opens no `GroupChannelCollection`, drains nothing, and
   * subscribes to no SDK events. Callers pass `isCurrentContextFilter` here so the (potentially
   * expensive) context-scoped drain runs only on the tab where the directory is actually rendered,
   * not on the "All" tab. Defaults to true.
   */
  enabled?: boolean;
}

/**
 * Data layer for the chat user directory (DA-19646).
 *
 * Opens a context-scoped Sendbird `GroupChannelCollection` (company id when no document is
 * open, `documentKey` when a document is open — mirroring the ChannelList current-context
 * query), drains every page, and derives the excluded-user set (peers already in a matching
 * direct chat) from the collection's live add/update/delete events. The eligible-user list is a
 * pure derivation: participants minus self, minus support users, minus the excluded set, then
 * name-filtered by the panel search text. `isFullyLoaded` flips true only after the last page
 * is retrieved and gates the directory render.
 *
 * Mirrors the drain + dispose + MutableRef (no `let`) + microtask-coalesced recompute lifecycle
 * of `useChatUnreadCounters`.
 */
const useChatUserDirectory = ({
  searchText,
  onSelect,
  enabled = true,
}: UseChatUserDirectoryArgs): UseChatUserDirectoryResult => {
  const sdk = useSendbirdStateContext().stores.sdkStore.sdk;
  const userProfile = useSelector(userProfileSelector);
  // Chat follows the company whose data the store holds, not the one in the URL: it keeps the
  // last company on pages that name none, and waits out a company switch until the new
  // company's chat users have loaded. A known compromise, to revisit together with the profile.
  const companyId = useSelector(loadedDataCompanyIdSelector);
  const documentId = useSelector(documentDocumentIdSelector);
  const participants = useSelector(chatParticipantsSelector) as List<ChatParticipantRecord>;

  const { formatMessage } = useIntl();
  const [isFullyLoaded, setIsFullyLoaded] = useState<boolean>(false);
  const [excludedGuids, setExcludedGuids] = useState<ReadonlySet<string>>(() => new Set());
  const [isCreating, setIsCreating] = useState<boolean>(false);
  // Synchronous interaction lock. A state-only guard would let two rapid clicks both read
  // `isCreating === false` before the first re-render commits; a ref flips immediately so the
  // second click is rejected (spec: "selecting another user row SHALL have no effect until the
  // in-progress creation resolves").
  const creatingRef = useRef<boolean>(false);

  useEffect(() => {
    setIsFullyLoaded(false);
    setExcludedGuids(new Set());
    // Do nothing unless enabled (e.g. the current-context tab is active): no collection, no drain,
    // no SDK subscriptions on the "All" tab where the directory is never rendered.
    if (!enabled || !sdk?.groupChannel || !sdk.currentUser || !companyId) return undefined;

    const currentUserGuid = userProfile.id;
    const filter = new GroupChannelFilter();
    filter.includeEmpty = true;
    // Context (companyId/documentId/type) lives in metadata; exclusion resolves it via
    // resolveChannelContext(cachedMetaData), so metadata must be loaded.
    filter.includeMetaData = true;
    // Directory is AP-scoped, matching the channels the chat surfaces (Apps.AP).
    filter.customTypesFilter = [Apps.AP];
    // Narrow SERVER-SIDE to the active context, mirroring ChannelList.buildQueryParams: on a
    // document page by the composite `documentKey` (`<companyId>/<documentId>`), else by
    // `companyId`. This keeps the drained collection scoped to the context so `isFullyLoaded`
    // means "every channel for THIS context is loaded", not company-wide.
    if (documentId) {
      filter.metadataKey = 'documentKey';
      filter.metadataValues = [`${companyId}/${documentId}`];
    } else {
      filter.metadataKey = 'companyId';
      filter.metadataValues = [companyId];
    }

    const collection: GroupChannelCollection = sdk.groupChannel.createGroupChannelCollection({ filter, limit: 100 });
    // Effect MutableRef cells (project style forbids `let`): `disposed` guards async work after
    // unmount; `scheduled` coalesces the SDK's burst of per-event fires into one recompute.
    const disposed = MutableRef.make(false);
    const scheduled = MutableRef.make(false);

    const recompute = (): void => {
      if (MutableRef.get(disposed)) return;
      setExcludedGuids(deriveExcludedGuids(collection.channels, currentUserGuid, companyId, documentId || null));
    };

    const scheduleRecompute = (): void => {
      if (MutableRef.get(scheduled)) return;
      MutableRef.set(scheduled, true);
      // Microtask: collapse the multiple fires Sendbird emits for a single change into one
      // recompute (a new direct channel added, a peer's channel deleted, metadata written).
      Promise.resolve().then(() => {
        MutableRef.set(scheduled, false);
        recompute();
      });
    };

    const handler: GroupChannelCollectionEventHandler = {
      // A direct chat appearing (created by me, or by a peer inviting me via a remote event)
      // adds that peer's GUID to the excluded set → the peer drops off the user directory.
      // No chat-list refresh is triggered here: the context is dual-written to the atomic `data`
      // string on the channel, which resolveChannelContext reads first, so the shared UIKit
      // <GroupChannelList> filters the new row in on its own add-driven re-render (DA-19646).
      onChannelsAdded: (_ctx, _channels: BaseChannel[]) => scheduleRecompute(),
      // Metadata/member changes can change whether a channel matches the context (e.g. metadata
      // written post-create, or a group↔member change), so recompute here too.
      onChannelsUpdated: (_ctx, _channels: BaseChannel[]) => scheduleRecompute(),
      // A channel leaving the context (deleted/left) drops its peer's GUID → the peer reappears.
      onChannelsDeleted: (_ctx, _urls: string[]) => scheduleRecompute(),
    };
    collection.setGroupChannelCollectionHandler(handler);

    // Drain every page (recursion, not a `while` loop, per the no-loop style), recomputing after
    // each page so the excluded set fills in progressively. `isFullyLoaded` only flips after the
    // last page, gating the directory render.
    const loadRemaining = async (): Promise<void> => {
      if (MutableRef.get(disposed) || !collection.hasMore) return;
      await collection.loadMore();
      recompute();
      await loadRemaining();
    };

    const loadAll = async (): Promise<void> => {
      try {
        recompute();
        await loadRemaining();
        if (!MutableRef.get(disposed)) {
          setIsFullyLoaded(true);
        }
      } catch (e) {
        console.error('[useChatUserDirectory] Failed to load channels for directory:', e);
      }
    };
    loadAll();

    return () => {
      MutableRef.set(disposed, true);
      collection.dispose();
    };
  }, [enabled, sdk, companyId, documentId, userProfile.id]);

  const eligibleUsers = useMemo<ReadonlyArray<DirectoryUser>>(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    return pipe(
      participants.toArray(),
      A.map(toDirectoryUser),
      // Exclude the current user and support users (interview decision: exclude self + support).
      A.filter((user) => user.userGUID !== userProfile.id && !user.isSupport),
      // Exclude peers already in a matching direct chat in the active context.
      A.filter((user) => !excludedGuids.has(user.userGUID)),
      // Filter by the panel's (debounced) search text, matching the chat-list search semantics.
      A.filter((user) => (normalizedSearch ? directoryUserName(user).toLowerCase().includes(normalizedSearch) : true)),
    );
  }, [participants, excludedGuids, userProfile.id, searchText]);

  // Start (or open the existing) direct chat with a user, reusing the exact single-user branch
  // of the ChannelList create flow: buildChannelContext(direct) → dedupe an existing matching
  // direct channel → createChannel → createMetaData → select. Kept identical to the panel's
  // create path so dedupe/context-metadata behaviour can never diverge (design Decision).
  const startChat = useCallback(
    async (user: DirectoryUser): Promise<void> => {
      // Interaction lock: ignore clicks while a creation is in flight, and bail without a company
      // context (creation is only possible inside a company).
      if (creatingRef.current || !sdk?.groupChannel || !companyId) return;
      creatingRef.current = true;
      setIsCreating(true);

      const invitee = user.userGUID;
      try {
        const { customType, metadata, data } = buildChannelContext(Apps.AP, companyId, documentId, 'direct');

        // Dedupe: if a matching direct channel already exists in this context, open it instead of
        // creating a duplicate (spec: "open that existing chat rather than create a duplicate").
        const query = sdk.groupChannel.createMyGroupChannelListQuery({
          customTypesFilter: [Apps.AP],
          includeEmpty: true,
          userIdsFilter: {
            userIds: [invitee, userProfile.id],
            includeMode: false,
            queryType: QueryType.AND,
          },
        });

        const findExisting = async (): Promise<GroupChannel | undefined> => {
          if (!query.hasNext) return undefined;
          const page = await query.next();
          const match = page.find((ch) => {
            const ctx = resolveChannelContext(ch);
            return (
              ctx?.type === 'direct' &&
              String(ctx?.companyId ?? '') === String(companyId) &&
              String(ctx?.documentId ?? '') === String(documentId ?? '')
            );
          });
          return match ?? (await findExisting());
        };

        const existing = await findExisting();
        if (existing) {
          // Existing match already carries context metadata, so it is already in the filtered
          // list; just open it and drop the peer from the directory.
          setExcludedGuids((prev) => new Set(prev).add(invitee));
          onSelect(existing.url);
          return;
        }

        // Dual-write context: `data` at create time (ATOMIC — part of the createChannel call), and
        // `metadata` after (the SDK createChannel takes no metadata argument). The context is
        // resolvable from `data` the instant the channel exists, so resolveChannelContext (which
        // reads `data` first) lets the shared chat list filter the new row in immediately — no
        // re-query needed while `metadata` catches up.
        const channel = await sdk.groupChannel.createChannel({
          invitedUserIds: [invitee, userProfile.id],
          operatorUserIds: [userProfile.id],
          customType,
          isDistinct: false,
          data,
        });
        await channel.createMetaData(metadata);

        // Optimistically exclude the invitee NOW (do not wait for the collection's onChannelsAdded,
        // which may never fire for a channel that had no matching metadata at creation): the peer
        // leaves the directory immediately on success.
        setExcludedGuids((prev) => new Set(prev).add(invitee));

        // The new AP channel auto-joins the useChatUnreadCounters collection (its filter is
        // customType+includeEmpty, with no metadata filter, so the channel matches at creation).
        // It also surfaces in the context-filtered chat list on the shared collection's add event
        // because its context is resolvable from the atomic `data` string (resolveChannelContext).
        onSelect(channel.url);
      } catch (e) {
        // Failure: clear the loader, leave the user in the directory (the excluded set never
        // gained this GUID, so no restore is needed), and surface an error notification.
        console.error('[useChatUserDirectory] Failed to start chat:', e);
        yieldErrorNotification(
          formatMessage({
            id: 'chat.userDirectory.startChatError',
            defaultMessage: 'Could not start the chat. Please try again.',
          }),
        );
      } finally {
        creatingRef.current = false;
        setIsCreating(false);
      }
    },
    [sdk, companyId, documentId, userProfile.id, onSelect, formatMessage],
  );

  return { isFullyLoaded, eligibleUsers, isCreating, startChat };
};

/**
 * A GUID→user lookup over the company chat participants, keyed by the Sendbird user id
 * (`userGUID`, which matches a channel member's `userId`). Reuses the same `toDirectoryUser`
 * projection as the directory so the name/email/picture a caller shows can never diverge from
 * the directory list. Used to enrich the mention suggestion row (name + email) in place of the
 * raw Sendbird user id.
 */
export const useChatUserLookup = (): ReadonlyMap<string, DirectoryUser> => {
  const participants = useSelector(chatParticipantsSelector) as List<ChatParticipantRecord>;
  return useMemo(
    () =>
      pipe(
        participants.toArray(),
        A.map(toDirectoryUser),
        A.map((user) => [user.userGUID, user] as const),
        (entries) => new Map(entries),
      ),
    [participants],
  );
};

export default useChatUserDirectory;
